import { useEffect, useState, useRef, useCallback } from "react";
import type { Business } from "lens-platform-sdk";
import { LensSDKException } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";

/**
 * Get user's business entity (Lens Business IDs) by business entity ID.
 */
export const useGetBusiness = (businessId?: string) => {
  const lensPlatformClient = useLensPlatformClient();

  const [business, setBusiness] = useState<Business>();

  const [error, setError] = useState<LensSDKException | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const getBusiness = useCallback(
    async (businessId?: string) => {
      if (businessId) {
        try {
          if (mounted.current) {
            setLoading(true);
          }
          const response = await lensPlatformClient.business.getOne(businessId);

          if (mounted.current) {
            setBusiness(response);
            setError(null);
          }

          return response;
        } catch (exception) {
          if (mounted.current && exception instanceof LensSDKException) {
            setError(exception);
          }
        } finally {
          if (mounted.current) {
            setLoading(false);
          }
        }
      }

      return undefined;
    },
    [lensPlatformClient.business],
  );

  useEffect(() => {
    // Load businesses on mount
    getBusiness(businessId);
  }, [getBusiness, businessId]);

  return {
    loading,
    error,
    business,
    reload: getBusiness,
  };
};
